import React from 'react';
import Joi from 'joi';
import getConfig from 'next/config';
import {
	api as apiRequest,
	batchApi as batchApiRequest
} from '@culture/common-helpers/request';
import { addCleanCache } from '@culture/common-helpers/params';
import _ from 'underscore';

import Cookies from 'cookies';
import Template from 'pageTemplates/traditions/main';
import {
	catalogMainBlockFields,
	instituteBlockFields,
	objectsFields
} from 'utils/helpers/blocks';
import {
	collectResultIdsInCookies,
	getEntityIdsFromCookies,
	getRecommendationsRequest,
	getRecommendationsResultsFromCookies
} from 'utils/helpers/recommendations';

const { serverRuntimeConfig } = getConfig();
const rubricId = serverRuntimeConfig.entities?.traditionsRubric;
const recommendationsEntityType = 'objects';
const recommendationPageEntityName = `traditions/${recommendationsEntityType}`;

const TraditionsPage = (props) => <Template {...props} />;

const getRubricPage = (rubricPath, cleanCache) =>
	apiRequest(
		'rubricPages/page',
		addCleanCache(
			{
				rubricPath,
				blockFields: {
					...catalogMainBlockFields,
					cultureHouses: instituteBlockFields.join(),
					religionHeritageObjects: objectsFields.join(),
					foreignHeritageObjects: objectsFields.join(),
					cultureHeritageObjects: objectsFields.join()
				},
				completeBlocksParams: [
					{
						blockName: 'articles',
						blockParams: {
							statuses: ['published'],
							types: ['articles'],
							rubricPaths: [serverRuntimeConfig.entities.traditionsRubric],
							sort: '-publishDate',
							limit: 5
						}
					},
					{
						blockName: 'cultureHeritageObjects',
						blockParams: {
							statuses: ['published'],
							rubricPaths: [serverRuntimeConfig.entities.traditionsRubric],
							sort: '-views',
							limit: 10
						}
					},
					{
						blockName: 'foreignHeritageObjects',
						blockParams: {
							statuses: ['published'],
							rubricPaths: [serverRuntimeConfig.entities.traditionsRubric],
							sort: '-views',
							limit: 10
						}
					},
					{
						blockName: 'religionHeritageObjects',
						blockParams: {
							statuses: ['published'],
							rubricPaths: [serverRuntimeConfig.entities.traditionsRubric],
							sort: '-views',
							limit: 10
						}
					},
					{
						blockName: 'cultureHouses',
						blockParams: {
							statuses: ['published'],
							types: ['hc'],
							rubricPaths: [serverRuntimeConfig.entities.traditionsRubric],
							sort: '-views',
							limit: 10
						}
					},
					{
						blockName: 'themes',
						blockParams: {
							statuses: ['published'],
							types: ['themes'],
							rubricPaths: [serverRuntimeConfig.entities.traditionsRubric],
							sort: '-publishDate',
							limit: 5
						}
					}
				]
			},
			cleanCache
		)
	);

const getRecommendedObjects = (_ids, cleanCache) => {
	const params = addCleanCache(
		{
			_ids,
			sort: '-views'
		},
		cleanCache
	);

	return apiRequest('objects', params);
};

const getRecommendations = async (cookies, params) => {
	const cachedRecommendationResultIds = getRecommendationsResultsFromCookies(
		cookies,
		recommendationPageEntityName
	);

	if (!_.isEmpty(cachedRecommendationResultIds)) {
		const institutes = await getRecommendedObjects(
			cachedRecommendationResultIds,
			params?.cleanCache
		);
		return institutes?.items || [];
	}
	const visitedEntitiesIds = getEntityIdsFromCookies(
		recommendationsEntityType,
		cookies
	);
	const recommendations = await getRecommendationsRequest({
		entityType: recommendationsEntityType,
		entityIds: visitedEntitiesIds,
		rubricId
	});

	if (!_.isEmpty(recommendations)) {
		const recommendationEntitiesIds = recommendations.map((item) => item._id);
		collectResultIdsInCookies(
			cookies,
			recommendationPageEntityName,
			recommendationEntitiesIds
		);
	}
	return recommendations;
};

export async function getServerSideProps({ query, req, res }) {
	const validationSchema = Joi.object().keys({
		cleanCache: Joi.boolean().failover(false)
	});

	const params = Joi.attempt(query, validationSchema, {
		stripUnknown: true
	});

	const rubricPath = 'traditions';

	const cookies = new Cookies(req, res);
	const recommendations = await getRecommendations(cookies, params);

	const [rubricPage] = await batchApiRequest([
		getRubricPage(rubricPath, params.cleanCache)
	]);

	return {
		props: {
			blocks: rubricPage.blocks,
			rubric: rubricPage.rubric,
			pageText: rubricPage.text,
			recommendations: recommendations || null
		}
	};
}

export default TraditionsPage;
